import { FeedbackKind, TriageStatus } from '@myrewards/contracts';

// The review list's filters live in the address, so a Home link (`?status=new`), a back button and
// a shared link all open the same view. Everything read from the address is checked first.

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type StatusFilter = TriageStatus | 'open' | 'all' | 'replies';

export interface FeedbackFilters {
  /**
   * `open` is New, In review and Planned: the default view. `replies` is every report with a
   * sender's reply the team hasn't read (APP-138 §D11).
   */
  status: StatusFilter;
  kind: FeedbackKind | null;
  area: string | null;
  /** A person's id, and their name for the chip (a name, never HTML). */
  from: { id: string; name: string } | null;
  sentFrom: string | null;
  sentTo: string | null;
  q: string;
}

export const DEFAULT_FILTERS: FeedbackFilters = {
  status: 'open',
  kind: null,
  area: null,
  from: null,
  sentFrom: null,
  sentTo: null,
  q: '',
};

function text(params: URLSearchParams, key: string, max: number): string | null {
  const value = params.get(key)?.trim();
  return value === undefined || value === '' ? null : value.slice(0, max);
}

function day(params: URLSearchParams, key: string): string | null {
  const value = text(params, key, 10);
  return value !== null && DATE.test(value) ? value : null;
}

export function filtersFrom(params: URLSearchParams): FeedbackFilters {
  const status = params.get('status');
  const parsedStatus = TriageStatus.safeParse(status);
  const kind = FeedbackKind.safeParse(params.get('kind'));
  const from = text(params, 'from', 36);
  return {
    status: parsedStatus.success
      ? parsedStatus.data
      : status === 'all' || status === 'replies'
        ? status
        : DEFAULT_FILTERS.status,
    kind: kind.success ? kind.data : null,
    area: text(params, 'area', 100),
    from:
      from !== null && UUID.test(from)
        ? { id: from, name: text(params, 'fromName', 120) ?? 'This person' }
        : null,
    sentFrom: day(params, 'sentFrom'),
    sentTo: day(params, 'sentTo'),
    q: text(params, 'q', 200) ?? '',
  };
}

/** The address for a set of filters; defaults are left out so the bare list stays bare. */
export function paramsFrom(filters: FeedbackFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.status !== DEFAULT_FILTERS.status) params.set('status', filters.status);
  if (filters.kind !== null) params.set('kind', filters.kind);
  if (filters.area !== null) params.set('area', filters.area);
  if (filters.from !== null) {
    params.set('from', filters.from.id);
    params.set('fromName', filters.from.name);
  }
  if (filters.sentFrom !== null) params.set('sentFrom', filters.sentFrom);
  if (filters.sentTo !== null) params.set('sentTo', filters.sentTo);
  if (filters.q !== '') params.set('q', filters.q);
  return params;
}

/** Whether anything narrows the view beyond the default one (what "Clear filters" undoes). */
export function isFiltered(filters: FeedbackFilters): boolean {
  return paramsFrom(filters).toString() !== '';
}

/** The server's query string for these filters. */
export function serverQuery(filters: FeedbackFilters): Record<string, string> {
  return {
    status: filters.status,
    ...(filters.kind !== null && { kind: filters.kind }),
    ...(filters.area !== null && { area: filters.area }),
    ...(filters.from !== null && { fromPersonId: filters.from.id }),
    ...(filters.sentFrom !== null && { sentFrom: filters.sentFrom }),
    ...(filters.sentTo !== null && { sentTo: filters.sentTo }),
    ...(filters.q !== '' && { q: filters.q }),
  };
}
